import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Chart from "@/components/Chart";
import BiasModal from "@/components/BiasModal";
import AlertsLog from "@/components/AlertsLog";
import SettingsPanel, { DEFAULT_VISUAL_TOGGLES, DEFAULT_SOUND_TOGGLES } from "@/components/SettingsPanel";
import { aggregate, tfStopDistance, tfsAvailableFor } from "@/lib/aggregator";
import { analyzeICT, classifyEntry } from "@/lib/ictEngine";
import * as Sound from "@/lib/soundAlerts";
import useMarketData, { PERIOD_OPTIONS, baseMinutesFor } from "@/hooks/useMarketData";
import useDragSLTP from "@/hooks/useDragSLTP";
import { buildPrompts } from "@/lib/quickPrompts";
import QuickPrompts from "@/components/QuickPrompts";
import DrawingToolkit from "@/components/DrawingToolkit";
import Brand from "@/components/Brand";
import AdSlot from "@/components/AdSlot";
import GlobalChat from "@/components/GlobalChat";
import DashboardModals from "@/components/DashboardModals";
import OrderPanel from "@/components/OrderPanel";
import MarkerJumpPopup from "@/components/MarkerJumpPopup";
import { ChallengeHud, ChallengeScoreModal } from "@/components/ChallengeHud";
import {
  TIER_RANK, FREE_SYMBOLS, FREE_PERIODS,
  loadPref, migrateLegacyVisualKeys,
} from "@/lib/dashboardPrefs";

migrateLegacyVisualKeys();

export default function Dashboard() {
  const { user, logout, refreshMe } = useAuth();
  const [bias, setBias] = useState(() => {
    try {
      const stored = localStorage.getItem("ict.bias.daily");
      if (!stored) return "";
      const { bias: b, date } = JSON.parse(stored);
      const today = new Date().toISOString().slice(0, 10);
      return date === today ? b : "";
    } catch { return ""; }
  });
  const [symbol, setSymbol] = useState(() => { try { return localStorage.getItem("ict.last.symbol") || "MNQ=F"; } catch { return "MNQ=F"; } });
  const [symbols, setSymbols] = useState([]);
  const [tf, setTf] = useState(() => { try { return localStorage.getItem("ict.last.tf") || "5m"; } catch { return "5m"; } });
  const [mode, setMode] = useState("replay");
  const [period, setPeriod] = useState("5d");
  const [streamIndex, setStreamIndex] = useState(() => { try { return parseInt(localStorage.getItem("ict.last.streamIndex")) || 40; } catch { return 40; } });
  const [speed, setSpeed] = useState(400);
  const [autoplay, setAutoplay] = useState(false);
  const [position, setPosition] = useState(() => { try { return JSON.parse(localStorage.getItem("ict.last.position")) || null; } catch { return null; } });
  const [alerts, setAlerts] = useState([]);
  const [soundOn, setSoundOn] = useState(true);
  const [riskPct, setRiskPct] = useState(1.0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("ict.sidebar.collapsed") === "1"; } catch { return false; }
  });
  const [visualToggles, setVisualToggles] = useState(() => loadPref("ict.visual", DEFAULT_VISUAL_TOGGLES));
  const [soundToggles, setSoundToggles] = useState(() => loadPref("ict.sounds", DEFAULT_SOUND_TOGGLES));
  const [soundTimeframeFilter, setSoundTimeframeFilter] = useState(() => loadPref("ict.sounds.tf", {}));
  const [tradeReview, setTradeReview] = useState(null);
  const [autoReview, setAutoReview] = useState(() => {
    try { return localStorage.getItem("ict.autoReview") !== "0"; } catch { return true; }
  });
  const [stats, setStats] = useState({ balance: user?.balance || 10000, win_rate: 0, total_trades: 0, total_pnl: 0, avg_win: 0, avg_loss: 0 });
  const [tradeHistory, setTradeHistory] = useState([]);
  const [plan, setPlan] = useState(() => { try { return JSON.parse(localStorage.getItem("ict.last.plan")) || null; } catch { return null; } });
  const [showPractice, setShowPractice] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [leaderboardInitialRange, setLeaderboardInitialRange] = useState("daily");
  const [showPerfCard, setShowPerfCard] = useState(false);
  const [showDefs, setShowDefs] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [drawActive, setDrawActive] = useState(false);
  const [drawOpen, setDrawOpen] = useState(false);
  const [smtMinimized, setSmtMinimized] = useState(() => { try { return localStorage.getItem("ict.smt.min") === "1"; } catch { return false; } });
  const [smtOverride, setSmtOverride] = useState(() => { try { return localStorage.getItem("ict.smt.override") || ""; } catch { return ""; } });
  const [showChallenge, setShowChallenge] = useState(false);
  const [showGuild, setShowGuild] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [markerPopup, setMarkerPopup] = useState(null);   // { marker, x, y } for click-to-jump TF picker
  const [pendingSeekTime, setPendingSeekTime] = useState(null);  // epoch sec to seek to after TF change
  const [bestPts, setBestPts] = useState(0);
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [challengeScore, setChallengeScore] = useState(null);
  const [challengeBars, setChallengeBars] = useState(null);
  const [showJumpDate, setShowJumpDate] = useState(false);
  const [unreadDMs, setUnreadDMs] = useState(0);

  // Load challenge "best" + active session — refreshes when modal closes
  useEffect(() => {
    if (showChallenge) return;
    (async () => {
      try {
        const [h, a] = await Promise.all([
          api.get("/challenges/history"),
          api.get("/challenges/active"),
        ]);
        const best = (h.data || []).reduce((m, c) => Math.max(m, (c.score?.total || 0)), 0);
        setBestPts(best);
        setActiveChallenge(a.data?.id ? a.data : null);
      } catch {}
    })();
  }, [showChallenge]);
  const [tier, setTier] = useState("free");
  const [gateMsg, setGateMsg] = useState("");
  const tierRank = TIER_RANK[tier] ?? 0;
  const isFree = tier === "free";
  const isElite = tier === "elite";

  const mainChartRef = useRef(null);
  const smtChartRef = useRef(null);
  const seenEventsRef = useRef(new Set());
  const autoTimerRef = useRef(null);
  const soundMuteUntilRef = useRef(0); // suppress sound bursts on data reload
  const [smtHeight, setSmtHeight] = useState(128); // px — resizable SMT pane

  // ---- Market data (real Yahoo bars via hook) ----
  const baseMin = baseMinutesFor(period);
  const availableTfs = tfsAvailableFor(baseMin);
  const onReset = useCallback((len) => {
    setStreamIndex(Math.min(60, len - 1));
    setPosition(null); setAlerts([]); seenEventsRef.current = new Set();
    // Silence event sounds for ~1.2s while ICT engine re-seeds historical events
    soundMuteUntilRef.current = Date.now() + 1200;
  }, []);
  const { baseBars, baseSmt, smtSymbol, loading, error, refetch } = useMarketData({ symbol, period, mode, smtOverride, onReset });

  // Keep current TF valid for the active base
  useEffect(() => {
    if (!availableTfs.includes(tf)) setTf(availableTfs[0] || "5m");
  }, [availableTfs, tf]);

  // Aggregate to selected TF
  const main = useMemo(() => aggregate(baseBars, tf, baseMin), [baseBars, tf, baseMin]);

  // Honor pending seek-to-time (set by marker click-to-jump after a TF switch).
  // We can't seek inline because `main` is recomputed after `tf` updates; this
  // effect waits until the new aggregation lands and then walks to the nearest bar.
  useEffect(() => {
    if (pendingSeekTime == null || !main?.length) return;
    let best = 0, bestDiff = Infinity;
    for (let i = 0; i < main.length; i++) {
      const d = Math.abs(main[i].time - pendingSeekTime);
      if (d < bestDiff) { bestDiff = d; best = i; }
    }
    setStreamIndex(best);
    setPendingSeekTime(null);
  }, [pendingSeekTime, main]);
  const smt = useMemo(() => aggregate(baseSmt, tf, baseMin), [baseSmt, tf, baseMin]);

  // Persist prefs
  useEffect(() => { localStorage.setItem("ict.visual", JSON.stringify(visualToggles)); }, [visualToggles]);
  useEffect(() => { localStorage.setItem("ict.sounds", JSON.stringify(soundToggles)); Sound.setPerEvent(soundToggles); }, [soundToggles]);
  useEffect(() => { localStorage.setItem("ict.sounds.tf", JSON.stringify(soundTimeframeFilter)); Sound.setPerTimeframe(soundTimeframeFilter); }, [soundTimeframeFilter]);
  useEffect(() => { Sound.setEnabled(soundOn); }, [soundOn]);

  // V2.8.8 — Daily Challenge auto-end at NY close (16:00 EST = session_end_epoch).
  // When the current bar's time crosses session_end_epoch, force-close any open position
  // at the bar's close price and auto-finish the challenge (locks the chart).
  useEffect(() => {
    if (!activeChallenge?.session_end_epoch) return;
    const bar = main[streamIndex];
    if (!bar) return;
    if (bar.time < activeChallenge.session_end_epoch) return;
    // Day is over.
    (async () => {
      try {
        if (position) {
          await finalizeTrade(bar.close, "Day Ended (NY Close)");
        }
        const { data: scored } = await api.post(`/challenges/${activeChallenge.id}/finish`);
        setActiveChallenge(null);
        setChallengeScore({ score: scored?.score, trades: scored?.trades, auto_ended: true });
        setAutoplay(false);
      } catch (e) { console.warn("Auto-end challenge failed:", e?.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamIndex, activeChallenge?.id, activeChallenge?.session_end_epoch]);

  // V2.6.8 — clear "disciplined" eligibility on browser back/forward during a challenge
  useEffect(() => {
    const onPop = () => { try { localStorage.setItem("ict.disciplined.eligible", "0"); } catch {} };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Load symbol list
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/market/symbols"); setSymbols(data); }
      catch (err) { console.warn("/market/symbols failed:", err.message); }
    })();
  }, []);

  // Load stats + trades + billing tier; auto-default Pro+ users to 60-day history
  useEffect(() => {
    (async () => {
      try {
        const [s, t, b] = await Promise.all([api.get("/stats"), api.get("/trades"), api.get("/billing")]);
        setStats(s.data); setTradeHistory(t.data);
        const userTier = b.data?.tier || "free";
        setTier(userTier);
        // First-load default: Pro+ gets 60d, free stays on 5d (already gated server-side)
        if (userTier !== "free") {
          const stored = localStorage.getItem("ict.period.user-preference");
          if (!stored) setPeriod("60d");
          else setPeriod(stored);
        } else {
          const stored = localStorage.getItem("ict.period.user-preference");
          if (stored) setPeriod(stored);
        }
      } catch (err) { console.warn("stats/trades/billing fetch failed:", err.message); }
    })();
  }, []);

  // Poll DM notifications every 20s. Use a ref to compare against the *latest*
  // count (closure over `unreadDMs` was always stale → triggered the bogus
  // "order fill" sound on every tick + on tab-resume catch-up).
  const unreadDMsRef = useRef(0);
  useEffect(() => { unreadDMsRef.current = unreadDMs; }, [unreadDMs]);
  const dmFirstTickRef = useRef(true);
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      // Skip while tab is hidden — otherwise we accumulate "new DM" sounds on resume
      if (document.visibilityState !== "visible") return;
      try {
        const { data } = await api.get("/social/notifications");
        if (cancelled) return;
        const prev = unreadDMsRef.current;
        if (!dmFirstTickRef.current && data.unread_messages > prev) {
          // play a short bell — NOT the order-fill chime
          try { new Audio("/sounds/message.mp3").play().catch(() => {}); } catch {}
        }
        dmFirstTickRef.current = false;
        setUnreadDMs(data.unread_messages);
      } catch {}
    }
    tick();
    const id = setInterval(tick, 20_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  useEffect(() => {
    try { localStorage.setItem("ict.sidebar.collapsed", sidebarCollapsed ? "1" : "0"); } catch {}
  }, [sidebarCollapsed]);

  // Persist trade context across navigation (Dashboard ↔ Account ↔ Admin ↔ back)
  useEffect(() => { try { localStorage.setItem("ict.last.symbol", symbol); } catch {} }, [symbol]);
  useEffect(() => { try { localStorage.setItem("ict.last.tf", tf); } catch {} }, [tf]);
  useEffect(() => { try { localStorage.setItem("ict.last.streamIndex", String(streamIndex)); } catch {} }, [streamIndex]);
  useEffect(() => {
    try {
      if (position) localStorage.setItem("ict.last.position", JSON.stringify(position));
      else localStorage.removeItem("ict.last.position");
    } catch {}
  }, [position]);
  useEffect(() => {
    try {
      if (plan) localStorage.setItem("ict.last.plan", JSON.stringify(plan));
      else localStorage.removeItem("ict.last.plan");
    } catch {}
  }, [plan]);

  // Surface fetch errors as alerts (and 402 gate messages as banner)
  useEffect(() => {
    if (!error) return;
    if (typeof error === "string" && /upgrade|pro|elite/i.test(error)) {
      setGateMsg(error);
      setTimeout(() => setGateMsg(""), 4500);
    }
    setAlerts(a => [...a, { id: `err-${Date.now()}`, kind: "LOSS", label: "Market data fetch failed", detail: error, barIndex: 0 }]);
  }, [error]);

  function tryPickSymbol(s) {
    if (isFree && !FREE_SYMBOLS.has(s)) {
      setGateMsg(`${s} requires Pro tier. Upgrade to unlock all symbols.`);
      setTimeout(() => setGateMsg(""), 4000);
      return;
    }
    setSymbol(s);
  }
  function tryPickPeriod(p) {
    if (isFree && !FREE_PERIODS.has(p)) {
      setGateMsg("60-day history requires Pro tier. Upgrade to unlock.");
      setTimeout(() => setGateMsg(""), 4000);
      return;
    }
    setPeriod(p);
    try { localStorage.setItem("ict.period.user-preference", p); } catch {}
  }
  function tryGoLive() {
    if (!isElite) {
      setGateMsg("Live mode requires Elite tier. Upgrade to access real-time polling.");
      setTimeout(() => setGateMsg(""), 4000);
      return;
    }
    setMode("live");
  }
  function tryOpenPractice() {
    if (tierRank < 1) {
      setGateMsg("Practice Mode requires Pro tier. Upgrade to access AI-generated quizzes.");
      setTimeout(() => setGateMsg(""), 4000);
      return;
    }
    setShowPractice(true);
  }
  function tryOpenPerfCard() {
    if (!isElite) {
      setGateMsg("Performance Card export requires Elite tier. Upgrade to share your stats.");
      setTimeout(() => setGateMsg(""), 4000);
      return;
    }
    setShowPerfCard(true);
  }

  const visible = useMemo(() => main.slice(0, streamIndex + 1), [main, streamIndex]);
  const visibleSmt = useMemo(() => smt.slice(0, streamIndex + 1), [smt, streamIndex]);
  const analysis = useMemo(() => analyzeICT(visible, visibleSmt), [visible, visibleSmt]);

  // Render chart
  useEffect(() => {
    mainChartRef.current?.setData(visible);
    smtChartRef.current?.setData(visibleSmt);
    drawOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, position, plan, analysis, visualToggles]);

  useEffect(() => {
    if (!mainChartRef.current) return;
    if (plan) mainChartRef.current.setDraggable({ entry: plan.entryPrice, sl: plan.stopLoss, tp: plan.takeProfit });
    else if (position) mainChartRef.current.setDraggable({ entry: position.entryPrice, sl: position.stopLoss, tp: position.takeProfit });
    else mainChartRef.current.setDraggable({ entry: null, sl: null, tp: null });
  }, [position, plan]);

  // Quick-prompts from current analysis
  const quickPrompts = useMemo(() => buildPrompts(analysis, visible), [analysis, visible]);

  // ICT events → alerts + sounds
  useEffect(() => {
    const newEvents = [];
    const push = (key, kind, label, detail, barIndex) => {
      if (seenEventsRef.current.has(key)) return;
      if (barIndex > streamIndex) return;
      seenEventsRef.current.add(key);
      newEvents.push({ id: key, kind, label, detail, barIndex });
      // Suppress audio during the post-reload silent window
      if (Date.now() >= soundMuteUntilRef.current) Sound.play(kind, tf);
    };
    analysis.fvgs.forEach(f => {
      if (f.index <= streamIndex)
        push(`fvg-${f.index}-${f.type}`, "FVG", `Fair Value Gap (${f.type})`, `Bar #${f.index} • ${f.bottom.toFixed(2)} → ${f.top.toFixed(2)}`, f.index);
      if (f.mitigated && f.mitigatedIndex <= streamIndex)
        push(`ifvg-${f.index}`, "IFVG", `Inverse FVG flipped`, `Polarity inverted @ bar #${f.mitigatedIndex}`, f.mitigatedIndex);
    });
    analysis.structure.forEach(s => {
      if (s.index <= streamIndex)
        push(`${s.type}-${s.index}-${s.direction}`, s.type, `${s.type} ${s.direction === "bullish" ? "↑" : "↓"}`, `Ref ${s.refPrice.toFixed(2)}`, s.index);
    });
    analysis.sweeps.forEach(sw => {
      if (sw.index <= streamIndex) {
        const kind = sw.type === "buyside" ? "SWEEP_BSL" : "SWEEP_SSL";
        push(`sweep-${sw.index}-${sw.type}`, kind, `Liquidity Purge (${sw.type === "buyside" ? "BSL" : "SSL"})`, `Took ${sw.level.toFixed(2)}`, sw.index);
      }
    });
    analysis.smt.forEach(sm => {
      if (sm.index <= streamIndex)
        push(`smt-${sm.index}-${sm.type}`, "SMT", `SMT Divergence (${sm.type.replace("_smt","")})`, `Against ${smtSymbol}`, sm.index);
    });
    analysis.sessions.forEach(ss => {
      if (ss.from <= streamIndex)
        push(`session-${ss.dayKey}-${ss.name}`, "SESSION", `${ss.name} session`, `H ${ss.high.toFixed(2)} • L ${ss.low.toFixed(2)}`, ss.from);
    });
    if (analysis.premiumDiscount) {
      const region = analysis.premiumDiscount.currentRegion;
      const key = `region-${Math.floor(streamIndex / 20)}-${region}`;
      if (!seenEventsRef.current.has(key)) {
        seenEventsRef.current.add(key);
        newEvents.push({ id: key, kind: region === "premium" ? "PREMIUM" : "DISCOUNT",
                         label: `Price in ${region}`, detail: `EQ ${analysis.premiumDiscount.eq.toFixed(2)}`, barIndex: streamIndex });
        if (Date.now() >= soundMuteUntilRef.current) Sound.play(region === "premium" ? "PREMIUM" : "DISCOUNT", tf);
      }
    }
    if (newEvents.length) setAlerts(a => [...a, ...newEvents].slice(-400));
  }, [streamIndex, analysis, smtSymbol]);

  useEffect(() => {
    if (autoplay && mode === "replay") {
      autoTimerRef.current = setInterval(() => {
        setStreamIndex(i => Math.min(i + 1, main.length - 1));
      }, speed);
    } else if (autoTimerRef.current) { clearInterval(autoTimerRef.current); autoTimerRef.current = null; }
    return () => { if (autoTimerRef.current) clearInterval(autoTimerRef.current); };
  }, [autoplay, speed, main.length, mode]);

  useEffect(() => {
    if (streamIndex >= main.length - 1 && autoplay) setAutoplay(false);
  }, [streamIndex, main.length, autoplay]);

  // Trade resolution
  useEffect(() => {
    if (!position) return;
    const bar = main[streamIndex];
    if (!bar) return;
    if (position.type === "BUY") {
      if (bar.low <= position.stopLoss) finalizeTrade(position.stopLoss, "Stop Loss");
      else if (bar.high >= position.takeProfit) finalizeTrade(position.takeProfit, "Take Profit");
    } else {
      if (bar.high >= position.stopLoss) finalizeTrade(position.stopLoss, "Stop Loss");
      else if (bar.low <= position.takeProfit) finalizeTrade(position.takeProfit, "Take Profit");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamIndex]);

  function selectBias(b) {
    setBias(b);
    try {
      localStorage.setItem("ict.bias.daily", JSON.stringify({
        bias: b, date: new Date().toISOString().slice(0, 10),
      }));
    } catch {}
    Sound.unlock();
  }


  function execute(side) {
    if (position) return;
    Sound.unlock();
    const bar = main[streamIndex]; if (!bar) return;
    const entryPrice = bar.close;
    const stopDist = tfStopDistance(visible, tf);
    const stopLoss = side === "BUY" ? entryPrice - stopDist : entryPrice + stopDist;
    const takeProfit = side === "BUY" ? entryPrice + stopDist * 2.5 : entryPrice - stopDist * 2.5;
    const dollarRisk = stats.balance * (riskPct / 100);
    const size = dollarRisk / (Math.abs(entryPrice - stopLoss) * 2);
    const ctx = classifyEntry(streamIndex, entryPrice, side, analysis);
    setPosition({ type: side, entryPrice, stopLoss, takeProfit, size, entryIndex: streamIndex, context: ctx });
    Sound.play("ORDER_FILL");
    setAlerts(a => [...a, { id: `order-${Date.now()}`, kind: "ORDER_FILL",
      label: `${side} filled @ ${entryPrice.toFixed(2)}`,
      detail: `Size ${size.toFixed(2)} • SL ${stopLoss.toFixed(2)} • TP ${takeProfit.toFixed(2)} • Risk ${riskPct}%`, barIndex: streamIndex }]);
  }

  const onDragLine = useDragSLTP(position, setPosition);
  // Plan drag: when a plan is active, the same drag handler moves the plan's lines instead.
  const onDragLineCombined = (which, price) => {
    if (plan) {
      setPlan(p => {
        if (!p) return p;
        if (which === "sl") {
          // V2.2.0: allow trailing SL above entry on long / below entry on short.
          return { ...p, stopLoss: price };
        }
        if (which === "tp") {
          if (p.side === "LONG"  && price <= p.entryPrice) return p;
          if (p.side === "SHORT" && price >= p.entryPrice) return p;
          return { ...p, takeProfit: price };
        }
        return p;
      });
    } else {
      onDragLine(which, price);
    }
  };

  function startPlan(side) {
    // Toggle: clicking the same side again cancels; clicking the other side switches.
    if (plan && plan.side === side) { setPlan(null); return; }
    const bar = main[streamIndex]; if (!bar) return;
    const entryPrice = bar.close;
    const stopDist = tfStopDistance(visible, tf);
    const stopLoss   = side === "LONG" ? entryPrice - stopDist : entryPrice + stopDist;
    const takeProfit = side === "LONG" ? entryPrice + stopDist * 2.5 : entryPrice - stopDist * 2.5;
    setPlan({ side, entryPrice, stopLoss, takeProfit });
    Sound.unlock();
  }
  function executePlan() {
    if (!plan) return;
    const side = plan.side === "LONG" ? "BUY" : "SELL";
    Sound.unlock();
    const dollarRisk = stats.balance * (riskPct / 100);
    const size = dollarRisk / (Math.abs(plan.entryPrice - plan.stopLoss) * 2);
    const ctx = classifyEntry(streamIndex, plan.entryPrice, side, analysis);
    setPosition({ type: side, entryPrice: plan.entryPrice, stopLoss: plan.stopLoss, takeProfit: plan.takeProfit, size, entryIndex: streamIndex, context: ctx });
    setPlan(null);
    Sound.play("ORDER_FILL");
    setAlerts(a => [...a, { id: `order-${Date.now()}`, kind: "ORDER_FILL",
      label: `${side} planned-fill @ ${plan.entryPrice.toFixed(2)}`,
      detail: `Size ${size.toFixed(2)} • SL ${plan.stopLoss.toFixed(2)} • TP ${plan.takeProfit.toFixed(2)} • R:R ${(Math.abs(plan.takeProfit - plan.entryPrice) / Math.abs(plan.entryPrice - plan.stopLoss)).toFixed(2)}`, barIndex: streamIndex }]);
  }

  async function finalizeTrade(exitPrice, reason) {
    const pos = position; if (!pos) return;
    const points = (exitPrice - pos.entryPrice) * (pos.type === "BUY" ? 1 : -1);
    const pnl = points * pos.size * 2;
    // V2.6.8 — custom execution sound also fires on SL/TP closes.
    Sound.play("ORDER_FILL");
    Sound.play(pnl >= 0 ? "PROFIT" : "LOSS");
    setAlerts(a => [...a, { id: `close-${Date.now()}`, kind: pnl >= 0 ? "PROFIT" : "LOSS",
      label: `${reason} ${pnl >= 0 ? "+" : ""}$${pnl.toFixed(2)}`, detail: `Exit ${exitPrice.toFixed(2)}`, barIndex: streamIndex }]);
    setPosition(null);
    try {
      const payload = {
        type: pos.type, entry_price: pos.entryPrice, stop_loss: pos.stopLoss, take_profit: pos.takeProfit,
        exit_price: exitPrice, size: pos.size, pnl, timeframe: tf, bias: bias || "Undetermined",
        entry_index: pos.entryIndex, exit_reason: reason,
        context: { ...pos.context, symbol, smt_symbol: smtSymbol, risk_pct: riskPct, mode, period },
      };
      const { data: trade } = await api.post("/trades", payload);
      if (autoReview) setTradeReview({ trade, analysis: null, loading: true });
      try {
        const { data: ai } = await api.post(`/trades/${trade.id}/analyze`);
        if (autoReview) setTradeReview({ trade, analysis: ai, loading: false });
      } catch (e) {
        const detail = e.response?.data?.detail || "AI analysis unavailable.";
        if (e.response?.status === 402) {
          setGateMsg(detail);
          setTimeout(() => setGateMsg(""), 5000);
        }
        if (autoReview) setTradeReview({ trade, analysis: { execution_alignment: detail, vulnerabilities: "—", psychology: "—", grade: "N/A" }, loading: false });
      }
      const [s, t] = await Promise.all([api.get("/stats"), api.get("/trades")]);
      setStats(s.data); setTradeHistory(t.data);
      refreshMe();
      // If this trade was inside a challenge, refresh active challenge and auto-finish at trade #4
      if (activeChallenge) {
        try {
          const { data: a } = await api.get("/challenges/active");
          const fresh = a?.id ? a : null;
          setActiveChallenge(fresh);
          if (fresh && (fresh.trade_ids || []).length >= (fresh.max_trades || 4)) {
            const { data: scored } = await api.post(`/challenges/${fresh.id}/finish`);
            setActiveChallenge(null);
            setChallengeScore({ score: scored?.score, trades: scored?.trades });
          }
        } catch {}
      }
    } catch (e) { console.error(e); }
  }

  function manualClose() { if (position) finalizeTrade(main[streamIndex].close, "Manual Liquidation"); }

  async function resetSession() {
    try {
      await api.post("/session/reset");
      const [s, t] = await Promise.all([api.get("/stats"), api.get("/trades")]);
      setStats(s.data); setTradeHistory(t.data);
      setPosition(null); setAlerts([]); seenEventsRef.current = new Set();
    } catch (err) { console.error("resetSession failed:", err.message); }
  }
  function resetChartView() { mainChartRef.current?.resetView(); smtChartRef.current?.resetView(); }

  function stepBack(n = 1) {
    setStreamIndex(i => Math.max(0, i - n));
    seenEventsRef.current = new Set();
    soundMuteUntilRef.current = Date.now() + 800;
  }
  function resetToStart() {
    setAutoplay(false);
    setStreamIndex(0);
    setPosition(null); setAlerts([]);
    seenEventsRef.current = new Set();
    soundMuteUntilRef.current = Date.now() + 1500;
    mainChartRef.current?.resetView(); smtChartRef.current?.resetView();
  }
  function jumpToSession(name) {
    // Find the most recent session of this name within current bars and scroll the chart.
    const ss = [...analysis.sessions].reverse().find(s => s.name === name && s.from <= streamIndex);
    if (!ss) return;
    setStreamIndex(Math.min(ss.from + 5, main.length - 1));
    if (main[ss.from] && main[Math.min(ss.to, main.length - 1)]) {
      mainChartRef.current?.setVisibleRange(main[ss.from].time - 600, main[Math.min(ss.to, main.length - 1)].time + 600);
      smtChartRef.current?.setVisibleRange(main[ss.from].time - 600, main[Math.min(ss.to, main.length - 1)].time + 600);
    }
  }
  async function setCapital(amount) {
    try {
      await api.post("/account/capital", { amount });
      const s = await api.get("/stats");
      setStats(s.data); refreshMe();
      setAlerts(a => [...a, { id: `cap-${Date.now()}`, kind: "ORDER_FILL",
        label: `Starting capital set to $${amount.toLocaleString()}`, detail: "Balance & trade history reset.", barIndex: streamIndex }]);
    } catch (e) { console.error(e); }
  }
  function exportCSV() {
    if (!tradeHistory.length) return;
    const headers = ["created_at","symbol","timeframe","bias","type","entry_price","exit_price","stop_loss","take_profit","size","pnl","exit_reason","grade"];
    const rows = tradeHistory.map(t => [
      t.created_at, t.context?.symbol || symbol, t.timeframe, t.bias, t.type,
      t.entry_price, t.exit_price, t.stop_loss, t.take_profit, t.size, t.pnl, t.exit_reason,
      t.analysis?.grade || "",
    ].map(v => JSON.stringify(v == null ? "" : v)).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ict-trades-${Date.now()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  function drawOverlays() {
    const mc = mainChartRef.current;
    if (!mc || !main.length) return;
    mc.clearOverlays(); mc.clearPriceLines();
    smtChartRef.current?.clearOverlays();

    if (visualToggles.fvg || visualToggles.ifvg) {
      analysis.fvgs.forEach(f => {
        if (f.index > streamIndex) return;
        if (!visualToggles.fvg && !f.mitigated) return;
        if (!visualToggles.ifvg && f.mitigated) return;
        const startTime = main[f.index].time;
        const endTime = main[Math.min(streamIndex, main.length - 1)].time;
        if (startTime === endTime) return;
        const color = f.mitigated ? "rgba(249,115,22,0.55)" : "rgba(234,179,8,0.55)";
        mc.addLineOverlay([{ time: startTime, value: f.top }, { time: endTime, value: f.top }], color);
        mc.addLineOverlay([{ time: startTime, value: f.bottom }, { time: endTime, value: f.bottom }], color);
      });
    }
    if (visualToggles.pd && analysis.premiumDiscount) {
      mc.addPriceLine({ price: analysis.premiumDiscount.eq, color: "rgba(168,85,247,0.75)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "EQ 50%" });
      mc.addPriceLine({ price: analysis.premiumDiscount.high, color: "rgba(244,63,94,0.6)",  lineWidth: 1, lineStyle: 1, axisLabelVisible: true, title: "Range High" });
      mc.addPriceLine({ price: analysis.premiumDiscount.low,  color: "rgba(16,185,129,0.6)", lineWidth: 1, lineStyle: 1, axisLabelVisible: true, title: "Range Low" });
    }
    if (visualToggles.sessions) {
      analysis.sessions.forEach(ss => {
        if (ss.from > streamIndex) return;
        const startTime = main[ss.from].time;
        const endTime = main[Math.min(ss.to, streamIndex)].time;
        if (startTime === endTime) return;
        const color = ss.name === "Asia" ? "rgba(59,130,246,0.4)" : ss.name === "London" ? "rgba(234,179,8,0.4)" : "rgba(244,114,182,0.4)";
        mc.addLineOverlay([{ time: startTime, value: ss.high }, { time: endTime, value: ss.high }], color);
        mc.addLineOverlay([{ time: startTime, value: ss.low  }, { time: endTime, value: ss.low  }], color);
      });
    }
    if (position) {
      // Entry-anchored TP/SL: draw filled risk + reward bands from entry-bar → current bar
      const entryBar = main[position.entryIndex] || main[streamIndex];
      const endBar = main[streamIndex];
      if (entryBar && endBar && entryBar.time !== endBar.time) {
        // Risk band (entry → SL) in red
        mc.addLineOverlay([{ time: entryBar.time, value: position.entryPrice }, { time: endBar.time, value: position.entryPrice }], "rgba(244,63,94,0.0)");
        mc.addLineOverlay([{ time: entryBar.time, value: position.stopLoss }, { time: endBar.time, value: position.stopLoss }], "rgba(244,63,94,0.75)");
        mc.addLineOverlay([{ time: entryBar.time, value: position.takeProfit }, { time: endBar.time, value: position.takeProfit }], "rgba(16,185,129,0.75)");
      }
      mc.addPriceLine({ price: position.entryPrice, color: "#94a3b8", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: `${position.type} ${position.entryPrice.toFixed(2)}` });
      mc.addPriceLine({ price: position.stopLoss,   color: "#f43f5e", lineWidth: 2, lineStyle: 0, axisLabelVisible: true, title: `SL ${position.stopLoss.toFixed(2)}` });
      mc.addPriceLine({ price: position.takeProfit, color: "#10b981", lineWidth: 2, lineStyle: 0, axisLabelVisible: true, title: `TP ${position.takeProfit.toFixed(2)}` });
    }
    if (plan && !position) {
      mc.addPriceLine({ price: plan.entryPrice, color: "#60a5fa", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: `${plan.side} ENTRY (plan)` });
      mc.addPriceLine({ price: plan.stopLoss,   color: "#f43f5e", lineWidth: 2, lineStyle: 1, axisLabelVisible: true, title: "◆ PLAN SL (drag)" });
      mc.addPriceLine({ price: plan.takeProfit, color: "#10b981", lineWidth: 2, lineStyle: 1, axisLabelVisible: true, title: "◆ PLAN TP (drag)" });
    }
    const markers = [];
    if (visualToggles.structure) analysis.structure.forEach(s => {
      if (s.index > streamIndex) return;
      markers.push({ time: main[s.index].time, position: s.direction === "bullish" ? "belowBar" : "aboveBar",
        color: s.type === "MSS" ? "#d946ef" : "#eab308", shape: s.direction === "bullish" ? "arrowUp" : "arrowDown", text: s.type });
    });
    if (visualToggles.sweeps) analysis.sweeps.forEach(sw => {
      if (sw.index > streamIndex) return;
      markers.push({ time: main[sw.index].time, position: sw.type === "buyside" ? "aboveBar" : "belowBar",
        color: "#38bdf8", shape: "circle", text: sw.type === "buyside" ? "BSL✗" : "SSL✗" });
    });
    if (visualToggles.smt) analysis.smt.forEach(sm => {
      if (sm.index > streamIndex) return;
      markers.push({ time: main[sm.index].time, position: sm.type === "bullish_smt" ? "belowBar" : "aboveBar",
        color: "#f472b6", shape: "square", text: "SMT" });
    });
    if (visualToggles.swings) {
      analysis.swings.swingHighs.forEach(s => { if (s.index <= streamIndex - 2)
        markers.push({ time: main[s.index].time, position: "aboveBar", color: "rgba(148,163,184,0.7)", shape: "arrowDown", text: "SH" }); });
      analysis.swings.swingLows.forEach(s => { if (s.index <= streamIndex - 2)
        markers.push({ time: main[s.index].time, position: "belowBar", color: "rgba(148,163,184,0.7)", shape: "arrowUp", text: "SL" }); });
    }
    const uniq = Object.values(markers.reduce((acc, m) => { acc[`${m.time}-${m.position}-${m.text}`] = m; return acc; }, {}))
                       .sort((a, b) => a.time - b.time);
    mc.setMarkers(uniq);
  }

  const progressPct = main.length ? ((streamIndex + 1) / main.length) * 100 : 0;
  const livePnL = position && main[streamIndex] ? ((main[streamIndex].close - position.entryPrice) * (position.type === "BUY" ? 1 : -1)) * position.size * 2 : 0;

  return (
    <div className="h-screen flex flex-col bg-[#0b0e14] text-slate-200">
      {!bias && <BiasModal onSelect={selectBias} />}
      <DashboardModals
        tradeReview={tradeReview} onCloseTradeReview={() => setTradeReview(null)}
        showPractice={showPractice} onClosePractice={() => setShowPractice(false)}
        practiceCtx={{
          symbol, timeframe: tf, smt_symbol: smtSymbol,
          last_close: main[streamIndex]?.close || 0,
          range_high: analysis.premiumDiscount?.high || 0,
          range_low:  analysis.premiumDiscount?.low  || 0,
          premium_discount_region: analysis.premiumDiscount?.currentRegion,
          fvgs: analysis.fvgs.filter(f => f.index <= streamIndex).slice(-6),
          sweeps: analysis.sweeps.filter(s => s.index <= streamIndex).slice(-4),
          structure: analysis.structure.filter(s => s.index <= streamIndex).slice(-5),
          smt: analysis.smt.filter(s => s.index <= streamIndex).slice(-3),
          sessions: analysis.sessions.slice(-3),
        }}
        showLeaderboard={showLeaderboard} onCloseLeaderboard={() => setShowLeaderboard(false)}
        leaderboardInitialRange={leaderboardInitialRange}
        showGuild={showGuild} tier={tier} onCloseGuild={() => setShowGuild(false)}
        showUserSearch={showUserSearch} onCloseUserSearch={() => setShowUserSearch(false)}
        showPerfCard={showPerfCard} stats={stats} tradeHistory={tradeHistory} user={user}
        onClosePerfCard={() => setShowPerfCard(false)}
        showDefs={showDefs} onCloseDefs={() => setShowDefs(false)}
        showJournal={showJournal} onCloseJournal={() => setShowJournal(false)}
        showChallenge={showChallenge} onCloseChallenge={() => setShowChallenge(false)}
        onChallengeOpenLeaderboard={(range) => { setLeaderboardInitialRange(range || "challenge"); setShowLeaderboard(true); }}
        onChallengeStarted={(c) => {
          setActiveChallenge(c);
          // V2.6.8 — Disciplined achievement tracking: only fires if no popstate during run.
          try { localStorage.setItem("ict.disciplined.eligible", "1"); } catch {}
          const epoch = c?.session_start_epoch;
          if (epoch && main?.length) {
            let best = 0, bestDiff = Infinity;
            for (let i = 0; i < main.length; i++) {
              const d = Math.abs(main[i].time - epoch);
              if (d < bestDiff) { bestDiff = d; best = i; }
            }
            setStreamIndex(best);
          }
        }}
        showJumpDate={showJumpDate} onCloseJumpDate={() => setShowJumpDate(false)}
        periodDays={tier === "free" ? 30 : tier === "pro" ? 120 : 180}
        onJumpToDate={(iso) => {
          if (!main?.length) { setShowJumpDate(false); return; }
          const target = new Date(iso).getTime();
          let best = 0, bestDiff = Infinity;
          for (let i = 0; i < main.length; i++) {
            const t = (main[i].time * 1000);
            const d = Math.abs(t - target);
            if (d < bestDiff) { bestDiff = d; best = i; }
          }
          setStreamIndex(best);
          setShowJumpDate(false);
        }}
      />
      <GlobalChat />

      <header className="bg-[#11151d] border-b border-slate-800 px-4 py-2.5 shrink-0 flex justify-between items-center font-mono gap-3 flex-wrap">
        <div className="flex items-center space-x-3 flex-wrap">
          <div className="flex items-center space-x-2">
          <Brand />
          </div>
          <select data-testid="symbol-select" value={symbol} onChange={e => tryPickSymbol(e.target.value)}
            className="bg-[#161d28] border border-slate-700 text-xs font-bold text-emerald-400 rounded-md px-2 py-1 focus:outline-none cursor-pointer">
            {symbols.map(s => (
              <option key={s.symbol} value={s.symbol}>
                {`${s.symbol} — ${s.label}`}{isFree && !FREE_SYMBOLS.has(s.symbol) ? "  🔒 PRO" : ""}
              </option>
            ))}
          </select>
          <select data-testid="tf-select" value={tf} onChange={e => setTf(e.target.value)}
            className="bg-[#161d28] border border-slate-700 text-xs font-bold text-blue-400 rounded-md px-2 py-1 cursor-pointer">
            {availableTfs.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex bg-[#161d28] rounded-md border border-slate-700 overflow-hidden">
            <button data-testid="mode-replay" onClick={() => setMode("replay")}
              className={`px-2 py-1 text-[11px] font-bold ${mode === "replay" ? "bg-blue-600 text-white" : "text-slate-400"}`}>REPLAY</button>
            <button data-testid="mode-live" onClick={tryGoLive} title={isElite ? "" : "Elite tier required"}
              className={`px-2 py-1 text-[11px] font-bold relative ${mode === "live" ? "bg-emerald-600 text-white" : "text-slate-400"} ${!isElite ? "opacity-60" : ""}`}>
              DELAY {!isElite && <span className="ml-1 text-amber-300">🔒</span>}
            </button>
          </div>
          {mode === "replay" && (
            <select data-testid="period-select" value={period} onChange={e => tryPickPeriod(e.target.value)}
              className="bg-[#161d28] border border-slate-700 text-[11px] font-bold text-slate-300 rounded-md px-2 py-1">
              {PERIOD_OPTIONS.map(p => (
                <option key={p.value} value={p.value}>
                  {`${p.label} (${p.base} base)`}{isFree && !FREE_PERIODS.has(p.value) ? "  🔒 PRO" : ""}
                </option>
              ))}
            </select>
          )}
          <div className="text-xs text-slate-400">Bias: <span data-testid="display-bias" className="text-blue-400 font-bold uppercase">{bias || "—"}</span></div>
          <div className="text-xs text-slate-400">SMT: <span className="text-pink-400 font-bold">{smtSymbol}</span></div>
          <div className="text-xs text-slate-400">Bar <span className="text-blue-400 font-bold">{streamIndex + 1}</span>/<span className="text-slate-500">{main.length}</span></div>
          <button data-testid="daily-challenge-btn" onClick={() => setShowChallenge(true)}
            className="ml-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-slate-900 rounded-md text-[11px] font-black uppercase tracking-wider shadow-md shadow-amber-500/30 flex items-center gap-1.5">
            <i className="fa-solid fa-bolt"/>
            <span>Daily Challenge</span>
            {activeChallenge && <span className="text-[9px] bg-slate-900/40 text-amber-100 px-1.5 py-0.5 rounded">{(activeChallenge.trade_ids||[]).length}/{activeChallenge.max_trades}</span>}
          </button>
          {bestPts > 0 && (
            <div data-testid="best-pts-pill" className="px-2.5 py-1 bg-amber-500/10 border border-amber-500/30 rounded-md text-[10px] font-bold uppercase tracking-wider text-amber-300">
              <i className="fa-solid fa-trophy mr-1"/> Best: <span className="text-amber-200 tabular-nums">{bestPts}</span> pts
            </div>
          )}
          {loading && <div className="text-amber-400 text-xs animate-pulse"><i className="fa-solid fa-spinner fa-spin mr-1"/>Loading…</div>}
        </div>
        <div className="flex items-center space-x-2 text-xs">
          <div className="bg-slate-900/80 px-2.5 py-1.5 rounded-lg border border-slate-800/60">
            <span className="text-slate-500">Bal:</span> <span data-testid="metric-balance" className="text-emerald-400 font-bold">${stats.balance.toFixed(2)}</span>
          </div>
          <div className="bg-slate-900/80 px-2.5 py-1.5 rounded-lg border border-slate-800/60">
            <span className="text-slate-500">WR:</span> <span className="text-amber-400 font-bold">{stats.win_rate.toFixed(0)}%</span>
            <span className="text-slate-600 ml-1">({stats.total_trades})</span>
          </div>
          {position && (
            <div className={`px-2.5 py-1.5 rounded-lg border font-bold uppercase ${livePnL >= 0 ? "bg-emerald-600/10 border-emerald-500/30 text-emerald-300" : "bg-rose-600/10 border-rose-500/30 text-rose-300"}`}>
              uPnL: {livePnL >= 0 ? "+" : ""}${livePnL.toFixed(2)}
            </div>
          )}
          <div data-testid="position-badge" className={`px-2.5 py-1.5 rounded-lg border font-bold uppercase ${position ? (position.type === "BUY" ? "bg-emerald-600/20 border-emerald-500/30 text-emerald-400" : "bg-rose-600/20 border-rose-500/30 text-rose-400") : "bg-slate-800 border-slate-700 text-slate-400"}`}>
            {position ? `${position.type} ${position.size.toFixed(1)}x` : "FLAT"}
          </div>
          <button data-testid="logout-btn" onClick={logout} className="text-slate-500 hover:text-rose-400 ml-2"><i className="fa-solid fa-right-from-bracket"/></button>
          <a href="/account" data-testid="nav-account" className="ml-1 relative" title="Account">
            {user?.avatar
              ? <img src={user.avatar} alt="me" className="w-6 h-6 rounded-full object-cover border border-slate-700"/>
              : <i className="fa-solid fa-user text-slate-500 hover:text-blue-300"/>}
            {unreadDMs > 0 && (
              <span data-testid="dm-red-dot" className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-black w-3.5 h-3.5 rounded-full flex items-center justify-center animate-pulse">{unreadDMs > 9 ? "9+" : unreadDMs}</span>
            )}
          </a>
          {user?.role === "admin" && <a href="/admin" data-testid="nav-admin" className="text-amber-400 hover:text-amber-300 ml-1" title="Admin"><i className="fa-solid fa-shield-halved"/></a>}
          {tier !== "elite" && (
            <a href="/account?tab=plan" data-testid="upgrade-btn"
              className="ml-2 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 rounded-md text-[11px] font-black uppercase tracking-wider shadow-md shadow-amber-500/30 transition-all"
              title="Upgrade your subscription">
              <i className="fa-solid fa-crown mr-1"/> Upgrade
            </a>
          )}
        </div>
      </header>

      {/* Toolbar: Definitions / Practice / Leaderboard / Perf card */}
      <div className="bg-[#11151d] border-b border-slate-800 px-4 py-1.5 flex gap-2 shrink-0 font-mono text-[11px]">
        <button data-testid="open-definitions" onClick={() => setShowDefs(true)}
          className="px-2.5 py-1 bg-cyan-600/10 border border-cyan-500/30 text-cyan-300 rounded-md hover:bg-cyan-600/20 font-bold">
          <i className="fa-solid fa-book-open mr-1"/> DEFINITIONS
        </button>
        <button data-testid="open-practice" onClick={tryOpenPractice}
          className={`px-2.5 py-1 bg-purple-600/10 border border-purple-500/30 text-purple-300 rounded-md hover:bg-purple-600/20 font-bold ${tierRank < 1 ? "opacity-70" : ""}`}>
          <i className="fa-solid fa-graduation-cap mr-1"/> PRACTICE {tierRank < 1 && <span className="text-amber-300 ml-1">🔒</span>}
        </button>
        <button data-testid="open-journal" onClick={() => setShowJournal(true)}
          className="px-2.5 py-1 bg-indigo-600/10 border border-indigo-500/30 text-indigo-300 rounded-md hover:bg-indigo-600/20 font-bold">
          <i className="fa-solid fa-book mr-1"/> JOURNAL
        </button>
        <button data-testid="open-leaderboard" onClick={() => setShowLeaderboard(true)}
          className="px-2.5 py-1 bg-amber-600/10 border border-amber-500/30 text-amber-300 rounded-md hover:bg-amber-600/20 font-bold">
          <i className="fa-solid fa-trophy mr-1"/> LEADERBOARD
        </button>
        <button data-testid="open-guild" onClick={() => setShowGuild(true)}
          className="px-2.5 py-1 bg-indigo-600/10 border border-indigo-500/30 text-indigo-300 rounded-md hover:bg-indigo-600/20 font-bold">
          <i className="fa-solid fa-shield-halved mr-1"/> GUILD
        </button>
        <button data-testid="open-search" onClick={() => setShowUserSearch(true)}
          className="px-2.5 py-1 bg-slate-600/10 border border-slate-500/30 text-slate-300 rounded-md hover:bg-slate-600/20 font-bold">
          <i className="fa-solid fa-magnifying-glass mr-1"/> SEARCH
        </button>
        <button data-testid="open-perfcard" onClick={tryOpenPerfCard}
          className={`px-2.5 py-1 bg-blue-600/10 border border-blue-500/30 text-blue-300 rounded-md hover:bg-blue-600/20 font-bold ${!isElite ? "opacity-70" : ""}`}>
          <i className="fa-solid fa-id-card mr-1"/> PERFORMANCE CARD {!isElite && <span className="text-amber-300 ml-1">🔒</span>}
        </button>
        <div className="flex-1"/>
        {gateMsg
          ? <div data-testid="gate-msg" className="text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-md font-bold"><i className="fa-solid fa-lock mr-1"/>{gateMsg}</div>
          : <div className="text-slate-500"></div>}
      </div>

      <div className="h-1 bg-slate-900 relative shrink-0">
        <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500" style={{ width: `${progressPct}%` }}/>
      </div>

      <ChallengeHud activeChallenge={activeChallenge}
        onFinished={({ score, trades }) => {
          setActiveChallenge(null);
          setChallengeScore({ score, trades });
          // V2.6.8 — Disciplined: still eligible? (no back-nav during the run)
          try {
            if (localStorage.getItem("ict.disciplined.eligible") === "1") {
              api.post("/achievements/event", { event: "challenge_no_back" }).catch(() => {});
            }
            localStorage.removeItem("ict.disciplined.eligible");
          } catch {}
        }}/>

      <MarkerJumpPopup markerPopup={markerPopup} availableTfs={availableTfs} currentTf={tf} main={main}
        onClose={() => setMarkerPopup(null)}
        onSeek={(idx) => setStreamIndex(idx)}
        onSwitchTf={(targetTf, t) => { setTf(targetTf); if (t) setPendingSeekTime(t); }}/>

      <ChallengeScoreModal challengeScore={challengeScore} onClose={() => setChallengeScore(null)} />

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col p-3 bg-[#090b0f] relative overflow-hidden">
          {/* Sidebar toggle — anchored to the chart panel's right edge */}
          <button
            data-testid="sidebar-toggle"
            onClick={() => setSidebarCollapsed(v => !v)}
            title={sidebarCollapsed ? "Show trading panel" : "Hide trading panel"}
            className="absolute top-1/2 -translate-y-1/2 right-1 z-30 w-6 h-16 bg-slate-900/90 hover:bg-slate-800 border border-slate-700 rounded-l-md text-slate-300 hover:text-white text-xs flex items-center justify-center transition-all">
            <i className={`fa-solid ${sidebarCollapsed ? "fa-chevron-left" : "fa-chevron-right"}`}/>
          </button>
          <div className="flex-1 relative min-h-0 border border-slate-800/60 rounded-lg overflow-hidden">
            {visualToggles.pd && analysis.premiumDiscount && (
              <div className="absolute top-2 left-2 z-10 bg-slate-900/90 border border-slate-800/80 backdrop-blur rounded px-2.5 py-1 text-[10px] font-mono">
                <span className={analysis.premiumDiscount.currentRegion === "premium" ? "text-rose-400" : "text-emerald-400"}>
                  {analysis.premiumDiscount.currentRegion.toUpperCase()}
                </span>
              </div>
            )}
            {/* Chart-action toolbar — top horizontal, pushed further from price axis */}
            <div className="absolute top-2 right-28 z-10 flex items-center gap-1.5 font-mono">
              <button data-testid="reset-view-btn" onClick={resetChartView}
                className="bg-slate-900/90 hover:bg-slate-800 border border-slate-700 rounded px-2 py-1 text-[10px] text-slate-300 font-bold">
                <i className="fa-solid fa-arrows-rotate mr-1"/> RESET VIEW
              </button>
              <button data-testid="jump-date-btn" onClick={() => !activeChallenge && setShowJumpDate(true)} disabled={!!activeChallenge}
                title={activeChallenge ? "Locked during challenge" : "Jump to any date"}
                className={`border rounded px-2 py-1 text-[10px] font-bold ${activeChallenge ? "bg-slate-900/40 border-slate-800 text-slate-600 cursor-not-allowed" : "bg-slate-900/90 hover:bg-slate-800 border-slate-700 text-blue-300"}`}>
                <i className="fa-solid fa-calendar-plus mr-1"/> JUMP TO DATE
              </button>
              <button data-testid="bar1-btn" onClick={() => !activeChallenge && resetToStart()} disabled={!!activeChallenge}
                title={activeChallenge ? "Locked during challenge" : "Reset to bar 1"}
                className={`border rounded px-2 py-1 text-[10px] font-bold ${activeChallenge ? "bg-slate-900/40 border-slate-800 text-slate-600 cursor-not-allowed" : "bg-slate-900/90 hover:bg-slate-800 border-slate-700 text-slate-300"}`}>
                <i className="fa-solid fa-backward-fast mr-1"/> BAR 1
              </button>
              <button data-testid="step-back-btn" onClick={() => !activeChallenge && stepBack(1)} disabled={!!activeChallenge}
                title={activeChallenge ? "Locked during challenge" : "Step back one bar"}
                className={`border rounded px-2 py-1 text-[10px] font-bold ${activeChallenge ? "bg-slate-900/40 border-slate-800 text-slate-600 cursor-not-allowed" : "bg-slate-900/90 hover:bg-slate-800 border-slate-700 text-slate-300"}`}>
                <i className="fa-solid fa-backward-step mr-1"/> BACK
              </button>
              <div className="w-px h-5 bg-slate-700 mx-0.5"/>
              <button data-testid="jump-asia"   onClick={() => jumpToSession("Asia")}     className="bg-blue-900/40 hover:bg-blue-900/60 border border-blue-700/40 rounded px-2 py-1 text-[10px] text-blue-200 font-bold">ASIA</button>
              <button data-testid="jump-london" onClick={() => jumpToSession("London")}   className="bg-amber-900/40 hover:bg-amber-900/60 border border-amber-700/40 rounded px-2 py-1 text-[10px] text-amber-200 font-bold">LONDON</button>
              <button data-testid="jump-ny"     onClick={() => jumpToSession("New York")} className="bg-pink-900/40 hover:bg-pink-900/60 border border-pink-700/40 rounded px-2 py-1 text-[10px] text-pink-200 font-bold">NY OPEN</button>
              <div className="w-px h-5 bg-slate-700 mx-0.5"/>
              <button data-testid="open-draw" onClick={() => { setDrawOpen(true); setDrawActive(true); }}
                className="bg-amber-900/40 hover:bg-amber-900/60 border border-amber-700/40 rounded px-2 py-1 text-[10px] text-amber-200 font-bold">
                <i className="fa-solid fa-pencil mr-1"/> DRAW
              </button>
            </div>
            <Chart ref={mainChartRef} height="100%" onDragLine={onDragLineCombined}
              onMarkerClick={(marker, param) => {
                if (!marker?.text) return;
                // Position popup near the click within the chart container
                setMarkerPopup({
                  marker,
                  time: marker.time,
                  x: param?.point?.x ?? 100,
                  y: param?.point?.y ?? 100,
                  currentTf: tf,
                });
              }}
            />
            {drawOpen && (
              <DrawingToolkit
                active={drawActive}
                onClose={() => { setDrawOpen(false); setDrawActive(false); }}
                onToggleActive={() => setDrawActive(v => !v)}
              />
            )}
          </div>
          <div className="relative mt-2" style={{ height: smtMinimized ? 28 : smtHeight }}>
            {/* Vertical resize handle — disabled when minimized */}
            {!smtMinimized && (
              <div
                data-testid="smt-resize-handle"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startY = e.clientY; const startH = smtHeight;
                  const mv = (ev) => setSmtHeight(Math.max(64, Math.min(400, startH + (startY - ev.clientY))));
                  const up = () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
                  window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
                }}
                className="absolute -top-1.5 left-0 right-0 h-3 cursor-row-resize z-20 flex items-center justify-center group"
                title="Drag to resize SMT pane">
                <div className="w-12 h-1 bg-slate-700 group-hover:bg-blue-500 rounded-full transition-colors"/>
              </div>
            )}
            <div className="h-full relative border border-slate-800/60 rounded-lg overflow-hidden">
              <div className="absolute top-1 left-1 z-10 bg-slate-900/90 border border-slate-800/80 backdrop-blur rounded px-2 py-0.5 text-[10px] text-slate-400 font-mono flex items-center gap-2">
                <span>SMT:</span>
                <select data-testid="smt-symbol-select" value={smtOverride || smtSymbol} onChange={e => {
                  const v = e.target.value;
                  if (v === symbol) return; // can't pair with self
                  setSmtOverride(v);
                  try { localStorage.setItem("ict.smt.override", v); } catch {}
                }} className="bg-transparent text-pink-400 font-bold border-none focus:outline-none cursor-pointer">
                  {symbols.filter(s => s.symbol !== symbol).map(s => (
                    <option key={s.symbol} value={s.symbol}>{s.symbol}</option>
                  ))}
                </select>
                {smtOverride && (
                  <button data-testid="smt-reset" onClick={() => { setSmtOverride(""); try { localStorage.removeItem("ict.smt.override"); } catch {} }}
                    className="text-slate-500 hover:text-amber-300" title="Reset to default pair">
                    <i className="fa-solid fa-rotate-left"/>
                  </button>
                )}
              </div>
              <button data-testid="smt-minimize" onClick={() => {
                  setSmtMinimized(m => { const next = !m; try { localStorage.setItem("ict.smt.min", next ? "1" : "0"); } catch {}; return next; });
                }} title={smtMinimized ? "Expand SMT" : "Minimize SMT"}
                className="absolute top-1 right-1 z-10 bg-slate-900/90 border border-slate-800/80 rounded px-2 py-0.5 text-[10px] text-slate-400 hover:text-white font-mono">
                <i className={`fa-solid ${smtMinimized ? "fa-up-right-and-down-left-from-center" : "fa-minus"}`}/>
              </button>
              {!smtMinimized && <Chart ref={smtChartRef} height="100%" smt />}
            </div>
          </div>
        </div>

        <aside data-testid="trading-sidebar" className={`bg-[#11151d] border-l border-slate-800 flex flex-col font-mono shrink-0 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? "w-0 border-l-0" : "w-96"}`}>
          <div className="px-4 pt-3">
            <AdSlot tier={tier} slot="trader-sidebar"/>
          </div>
          <OrderPanel
            mode={mode} autoplay={autoplay} setAutoplay={setAutoplay} speed={speed} setSpeed={setSpeed}
            streamIndex={streamIndex} mainLen={main.length}
            advanceBar={() => setStreamIndex(i => Math.min(i + 1, main.length - 1))}
            refetch={refetch}
            riskPct={riskPct} setRiskPct={setRiskPct}
            position={position} plan={plan}
            onExecute={execute} onLiquidate={manualClose}
            onStartPlan={startPlan} onExecutePlan={executePlan}
            onDiscardPlan={() => setPlan(null)}
            onResetSession={resetSession} onExportCSV={exportCSV}
            onSetCapital={setCapital}
            lastClose={main[streamIndex]?.close} balance={stats.balance}
          />

          <SettingsPanel
            visualToggles={visualToggles} setVisualToggles={setVisualToggles}
            soundOn={soundOn} setSoundOn={setSoundOn}
            soundToggles={soundToggles} setSoundToggles={setSoundToggles}
            soundTimeframeFilter={soundTimeframeFilter} setSoundTimeframeFilter={setSoundTimeframeFilter}
            hidden
          />

          <div className="px-3 pt-3 border-t border-slate-800">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">
              <i className="fa-solid fa-bullseye text-blue-400 mr-1"/> ICT Setup Prompts
            </div>
            <QuickPrompts prompts={quickPrompts} currentIndex={streamIndex}
              onJump={(i) => setStreamIndex(i)}
              onPlan={(p) => startPlan(p.direction === "long" ? "LONG" : "SHORT")}/>
          </div>

          <div className="flex-1 p-3 overflow-hidden min-h-0">
            <AlertsLog alerts={alerts} onClear={() => setAlerts([])} soundOn={soundOn} onToggleSound={() => setSoundOn(v => !v)} />
          </div>

          <div className="p-3 border-t border-slate-800">
            <details>
              <summary className="text-[11px] text-slate-400 uppercase tracking-wider font-bold cursor-pointer">
                <i className="fa-solid fa-clock-rotate-left mr-1.5"/> Trade Ledger ({tradeHistory.length})
              </summary>
              <div data-testid="trade-history" className="max-h-40 overflow-y-auto mt-2 space-y-1 text-[10px]">
                {tradeHistory.length === 0 && <div className="text-slate-600 text-center py-2">No trades yet.</div>}
                {tradeHistory.slice(0, 10).map(t => (
                  <div key={t.id} className="flex justify-between bg-slate-900/60 border border-slate-800 rounded px-2 py-1">
                    <span className={t.type === "BUY" ? "text-emerald-400" : "text-rose-400"}>{t.type} {t.timeframe}</span>
                    <span className="text-slate-500">{t.context?.symbol || ""}</span>
                    <span className={t.pnl >= 0 ? "text-emerald-300" : "text-rose-300"}>${t.pnl.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        </aside>
      </div>
    </div>
  );
}
